import { z } from "zod";

/**
 * What a seller asks every buyer for, beyond the account itself.
 *
 * Name and email are not in this list: every booking needs both, for the
 * invoice and so the seller can find a person by more than a phone number. The
 * mobile number is the sign-in identity, so it is always on file too. These are
 * the extras an organiser, a venue or a seller can switch on, each one stored
 * on the profile so a person answers it once, not once per listing.
 *
 * Stored under a `bookingFields` key in the owning row's `metadata` JSON —
 * `event.metadata`, `venue_experience.metadata` and `shop_item.metadata` all
 * share this shape. A missing key reads as "off", so adding a field here needs
 * no migration and leaves every existing listing alone.
 */
export const BOOKING_FIELD_KEYS = [
  "gender",
  "dateOfBirth",
  "profilePhoto",
  "location",
] as const;

export type BookingFieldKey = (typeof BOOKING_FIELD_KEYS)[number];

export const BOOKING_FIELD_MODES = ["off", "optional", "required"] as const;

export type BookingFieldMode = (typeof BOOKING_FIELD_MODES)[number];

export type BookingFieldModes = Record<BookingFieldKey, BookingFieldMode>;

export const BOOKING_FIELD_LABELS: Record<BookingFieldKey, string> = {
  gender: "Gender",
  dateOfBirth: "Date of birth",
  profilePhoto: "Profile photo",
  location: "Location",
};

export const DEFAULT_BOOKING_FIELD_MODES: BookingFieldModes = {
  gender: "off",
  dateOfBirth: "off",
  profilePhoto: "off",
  location: "off",
};

const bookingFieldModeSchema = z.enum(BOOKING_FIELD_MODES);

export const bookingFieldModesSchema = z
  .object({
    gender: bookingFieldModeSchema.default("off"),
    dateOfBirth: bookingFieldModeSchema.default("off"),
    profilePhoto: bookingFieldModeSchema.default("off"),
    location: bookingFieldModeSchema.default("off"),
  })
  .default(DEFAULT_BOOKING_FIELD_MODES);

function readMode(value: unknown): BookingFieldMode {
  return BOOKING_FIELD_MODES.includes(value as BookingFieldMode)
    ? (value as BookingFieldMode)
    : "off";
}

/**
 * The field modes stored on a listing, with every gap filled as "off".
 *
 * Also reads the two booleans (`collectGender`, `collectProfilePhoto`) that a
 * pre-release build wrote before fields had modes; `true` there meant the
 * answer was demanded, so it reads as "required".
 */
export function readBookingFieldModes(
  metadata: Record<string, unknown> | null | undefined,
): BookingFieldModes {
  const stored = metadata?.bookingFields;
  const record =
    stored && typeof stored === "object" && !Array.isArray(stored)
      ? (stored as Record<string, unknown>)
      : {};
  return {
    gender:
      record.gender !== undefined
        ? readMode(record.gender)
        : metadata?.collectGender === true
          ? "required"
          : "off",
    dateOfBirth: readMode(record.dateOfBirth),
    profilePhoto:
      record.profilePhoto !== undefined
        ? readMode(record.profilePhoto)
        : metadata?.collectProfilePhoto === true
          ? "required"
          : "off",
    location: readMode(record.location),
  };
}

/** Listing metadata with the field modes written in their current shape. */
export function withBookingFieldModes(
  metadata: Record<string, unknown> | null | undefined,
  modes: BookingFieldModes,
): Record<string, unknown> {
  const {
    collectGender: _legacyGender,
    collectProfilePhoto: _legacyPhoto,
    ...rest
  } = metadata ?? {};
  return { ...rest, bookingFields: { ...modes } };
}

export type BookingProfileFacts = {
  gender: string | null;
  dateOfBirth: string | null;
  hasProfilePhoto: boolean;
  /** The profile's stored locality (`profile.locationDisplayName`). */
  location: string | null;
};

function hasAnswer(key: BookingFieldKey, facts: BookingProfileFacts) {
  switch (key) {
    case "gender":
      return Boolean(facts.gender);
    case "dateOfBirth":
      return Boolean(facts.dateOfBirth);
    case "profilePhoto":
      return facts.hasProfilePhoto;
    case "location":
      return Boolean(facts.location);
  }
}

/** Required fields this person has not answered. The booking authority. */
export function missingRequiredBookingFields(
  modes: BookingFieldModes,
  facts: BookingProfileFacts,
): BookingFieldKey[] {
  return BOOKING_FIELD_KEYS.filter(
    (key) => modes[key] === "required" && !hasAnswer(key, facts),
  );
}

/** Every switched-on field (required or optional) still unanswered. */
export function unansweredBookingFields(
  modes: BookingFieldModes,
  facts: BookingProfileFacts,
): BookingFieldKey[] {
  return BOOKING_FIELD_KEYS.filter(
    (key) => modes[key] !== "off" && !hasAnswer(key, facts),
  );
}

const MODE_RANK: Record<BookingFieldMode, number> = {
  off: 0,
  optional: 1,
  required: 2,
};

/**
 * The strictest mode per field across several listings — required beats
 * optional beats off. A multi-item cart asks for the union of what its items
 * want, so a required question on one item is not missed because another leaves
 * it off.
 */
export function mergeBookingFieldModes(
  list: Array<BookingFieldModes | null | undefined>,
): BookingFieldModes {
  const merged = { ...DEFAULT_BOOKING_FIELD_MODES };
  for (const modes of list) {
    if (!modes) continue;
    for (const key of BOOKING_FIELD_KEYS) {
      if (MODE_RANK[modes[key]] > MODE_RANK[merged[key]]) {
        merged[key] = modes[key];
      }
    }
  }
  return merged;
}

export function bookingFieldsMissingMessage(keys: BookingFieldKey[]): string {
  const labels = keys.map((key) => BOOKING_FIELD_LABELS[key].toLowerCase());
  const list =
    labels.length <= 1
      ? (labels[0] ?? "your details")
      : `${labels.slice(0, -1).join(", ")} and ${labels.at(-1)}`;
  return `Add your ${list} before booking.`;
}
